import { Nullable } from '@ui/types/utility';
import { isEmpty, isNil } from 'lodash-es';
import { TFunction } from 'next-i18next';

import { ErrorMsg } from '../components/ErrorMsg';
import { TpSlValueState } from '../types';

export type TpValidationResult = {
  valid: boolean;
  error?: TakeProfitPriceError;
};

export type TakeProfitPriceError =
  | 'should_be_above_entry_price'
  | 'should_be_below_entry_price'
  | 'should_be_above_0'
  | 'should_be_below_max_tp_ratios_price'
  | 'should_be_above_max_tp_ratios_price'
  | 'should_be_above_market_price'
  | 'should_be_below_market_price'
  | 'should_be_above_min_tp_pnl';

export const validateTakeProfitPrice = ({
  isLong,
  entryPrice,
  tp,
  maxTpRatios,
  maxTpRatiosPrice,
  marketPrice,
  isMarket = true,
}: {
  entryPrice: number | undefined;
  isLong: boolean;
  tp: TpSlValueState | null;
  maxTpRatios: Nullable<number>;
  maxTpRatiosPrice: Nullable<number>;
  marketPrice: Nullable<number>;
  isMarket?: boolean;
}): TpValidationResult => {
  if (
    isNil(tp) ||
    isEmpty(tp.priceValue) ||
    tp.priceValue == '0' ||
    isNil(entryPrice) ||
    isNil(maxTpRatiosPrice) ||
    isNil(maxTpRatios) ||
    isNil(marketPrice)
  ) {
    return {
      valid: true,
    };
  }

  // Data is old
  if (!isNil(tp.isLong) && tp.isLong !== isLong) {
    return {
      valid: true,
    };
  }

  if (Number(tp.priceValue) <= 0) {
    return {
      valid: false,
      error: 'should_be_above_0',
    };
  }

  // Add min 3% rule check
  if (!isNil(tp.pnlPercentage) && tp.pnlPercentage < 0.03) {
    return {
      valid: false,
      error: 'should_be_above_min_tp_pnl',
    };
  }

  if (isLong) {
    if (Number(tp.priceValue) <= entryPrice) {
      return {
        valid: false,
        error: 'should_be_above_entry_price',
      };
    }
    if (Number(tp.priceValue) <= marketPrice && isMarket) {
      // only check when is market order
      return {
        valid: false,
        error: 'should_be_above_market_price',
      };
    }
    if (!tp.isPnlPercentage && Number(tp.priceValue) > maxTpRatiosPrice) {
      return {
        valid: false,
        error: 'should_be_below_max_tp_ratios_price',
      };
    }
    if (tp.isPnlPercentage && Number(tp.pnlPercentage) > maxTpRatios) {
      return {
        valid: false,
        error: 'should_be_below_max_tp_ratios_price',
      };
    }
  } else {
    if (Number(tp.priceValue) >= entryPrice) {
      return {
        valid: false,
        error: 'should_be_below_entry_price',
      };
    }

    if (Number(tp.priceValue) >= marketPrice && isMarket) {
      // only check when is market order
      return {
        valid: false,
        error: 'should_be_below_market_price',
      };
    }

    if (!tp.isPnlPercentage && Number(tp.priceValue) < maxTpRatiosPrice) {
      return {
        valid: false,
        error: 'should_be_above_max_tp_ratios_price',
      };
    }
    if (tp.isPnlPercentage && Number(tp.pnlPercentage) > maxTpRatios) {
      return {
        valid: false,
        error: 'should_be_above_max_tp_ratios_price',
      };
    }
  }

  return {
    valid: true,
  };
};

export const getTakeProfitPriceErrorMessage = ({
  t,
  error,
  maxTpRatios,
}: {
  t: TFunction;
  error?: TakeProfitPriceError;
  maxTpRatios: Nullable<number>;
}) => {
  if (!error) {
    return undefined;
  }

  switch (error) {
    case 'should_be_above_market_price':
      return (
        <ErrorMsg
          errorMsg={t('common.Take Profit Price should be above market price')}
        />
      );
    case 'should_be_below_market_price':
      return (
        <ErrorMsg
          errorMsg={t('common.Take Profit Price should be below market price')}
        />
      );
    case 'should_be_above_entry_price':
      return (
        <ErrorMsg
          errorMsg={t('common.Take Profit Price should be above entry price')}
        />
      );
    case 'should_be_below_entry_price':
      return (
        <ErrorMsg
          errorMsg={t('common.Take Profit Price should be below entry price')}
        />
      );
    case 'should_be_above_0':
      return (
        <ErrorMsg errorMsg={t('common.Take Profit Price should be above 0')} />
      );
    case 'should_be_below_max_tp_ratios_price':
    case 'should_be_above_max_tp_ratios_price': {
      return (
        <ErrorMsg
          errorMsg={`Max TP PnL reached: ${
            maxTpRatios && maxTpRatios !== 0 ? `${maxTpRatios * 100}%` : '-'
          }`}
        />
      );
    }
    case 'should_be_above_min_tp_pnl':
      return <ErrorMsg errorMsg={t('common.Take Profit should be at least 3%')} />;
  }
};
